import type { QualifyBand } from "./qualifyLead";
import type { InquiryType } from "./types";

/**
 * U3 QUALIFY, the calibration audit hook (Agent Brains 5.3 L4: "calibration
 * audits on its own scores; unknown != assumed"). Pure, no IO: the loader
 * (`packages/db/src/measureQualifyCli.ts`) reads events and hands them in as
 * folds, so the arithmetic is deterministic code (CLAUDE.md section 4).
 *
 * ASSOCIATION, NOT LIFT. Nothing here chose who got which band, and nothing
 * routes on the band (`lead.score` gates nothing), so a band's win count says
 * what happened to leads that were scored so, and nothing about what the band
 * caused (the `salesMeasure.ts` wording).
 *
 * EVENTS ONLY. The inputs are the latest `lead.scored` per contact, the
 * outcome events folded through `outcome.reversed` and `outcome.ghost_reversed`
 * by outcome id, and owner verdicts. Events survive erasure with ids and codes
 * only, so the result depends on no field about a person: renaming every
 * contact id leaves it unchanged (pinned by test). The output carries counts
 * and no ids.
 *
 * ONE PLACE PER CONTACT. A contact is counted once, in the band of its latest
 * score before its first standing resolved outcome, or in its latest band when
 * it has none. A contact whose first outcome precedes every score is placed
 * nowhere: a band assigned after the fact is not a prediction. Untagged leads
 * are unresolved, ghosts are excluded (D28), and a score is never read as a
 * probability.
 *
 * SHOW RATE has no source yet, and auto-demote is a documented hook that
 * throws if called.
 */

export const QUALIFY_BANDS: readonly QualifyBand[] = Object.freeze(["priority", "standard", "low_signal", "unknown"] as const);

export type OutcomeFoldKind = "recorded" | "auto_ghost" | "reversed" | "ghost_reversed";
export type OutcomeFoldLabel = "sold" | "booked" | "deposit" | "ghosted" | "lost" | "ongoing";

export interface ScoredFold {
  readonly contactId: string;
  readonly band: QualifyBand;
  readonly occurredAt: Date;
}

export interface OutcomeFold {
  readonly contactId: string;
  /** The outcome's id: a reversal carries the id of the outcome it withdraws. */
  readonly outcomeId: string;
  readonly kind: OutcomeFoldKind;
  /** Null on a reversal. */
  readonly label: OutcomeFoldLabel | null;
  readonly occurredAt: Date;
}

export interface VerdictFold {
  readonly contactId: string;
  readonly verdict: "real_buyer" | "time_waster";
}

export interface QualifyCalibrationRow {
  readonly band: QualifyBand;
  /** Contacts placed in this band. */
  readonly scored: number;
  /** Of those, contacts with a standing resolved outcome (sold, booked, deposit or lost). */
  readonly resolved: number;
  /** Of those, contacts with a standing sold outcome. */
  readonly won: number;
  /** Of those, contacts with a standing booked outcome. */
  readonly booked: number;
  /** Placed, with no standing outcome and not a ghost. */
  readonly untaggedUnresolved: number;
  /** Placed, whose only standing outcome is a ghost: excluded from resolved. */
  readonly ghostsExcluded: number;
  readonly ownerVerdicts: { readonly realBuyer: number; readonly timeWaster: number };
}

const RESOLVED_LABELS: readonly OutcomeFoldLabel[] = ["sold", "booked", "deposit", "lost"];

interface Standing {
  readonly label: OutcomeFoldLabel;
  readonly occurredAt: Date;
}

/** The outcomes that still stand for each contact, a reversal removing its outcome by id wherever it arrives. */
function standingByContact(outcomes: readonly OutcomeFold[]): Map<string, Standing[]> {
  const withdrawn = new Set<string>();
  for (const o of outcomes) if (o.kind === "reversed" || o.kind === "ghost_reversed") withdrawn.add(o.outcomeId);
  const out = new Map<string, Standing[]>();
  for (const o of outcomes) {
    if ((o.kind !== "recorded" && o.kind !== "auto_ghost") || o.label === null || withdrawn.has(o.outcomeId)) continue;
    out.set(o.contactId, [...(out.get(o.contactId) ?? []), { label: o.label, occurredAt: o.occurredAt }]);
  }
  return out;
}

export function calibrateQualify(
  scored: readonly ScoredFold[],
  outcomes: readonly OutcomeFold[],
  verdicts: readonly VerdictFold[],
): readonly QualifyCalibrationRow[] {
  const standing = standingByContact(outcomes);
  const scoresByContact = new Map<string, ScoredFold[]>();
  for (const s of scored) scoresByContact.set(s.contactId, [...(scoresByContact.get(s.contactId) ?? []), s]);
  const latestAtOrBefore = (list: readonly ScoredFold[], at: Date | null): ScoredFold | null => {
    let best: ScoredFold | null = null;
    for (const s of list) {
      if (at !== null && s.occurredAt.getTime() > at.getTime()) continue;
      if (best === null || s.occurredAt.getTime() >= best.occurredAt.getTime()) best = s;
    }
    return best;
  };

  type MutableRow = {
    -readonly [K in keyof QualifyCalibrationRow]: QualifyCalibrationRow[K] extends object
      ? { -readonly [J in keyof QualifyCalibrationRow[K]]: QualifyCalibrationRow[K][J] }
      : QualifyCalibrationRow[K];
  };
  const rows = new Map<QualifyBand, MutableRow>();
  for (const band of QUALIFY_BANDS) {
    rows.set(band, {
      band,
      scored: 0,
      resolved: 0,
      won: 0,
      booked: 0,
      untaggedUnresolved: 0,
      ghostsExcluded: 0,
      ownerVerdicts: { realBuyer: 0, timeWaster: 0 },
    });
  }
  const placed = new Map<string, QualifyBand>();

  for (const [contactId, list] of scoresByContact) {
    const mine = standing.get(contactId) ?? [];
    const resolvedOnes = mine.filter((m) => RESOLVED_LABELS.includes(m.label));
    const firstResolvedAt =
      resolvedOnes.length === 0 ? null : new Date(Math.min(...resolvedOnes.map((m) => m.occurredAt.getTime())));
    const pick = latestAtOrBefore(list, firstResolvedAt);
    if (pick === null) continue; // the first outcome precedes every score: a band after the fact predicts nothing
    const row = rows.get(pick.band)!;
    placed.set(contactId, pick.band);
    row.scored += 1;
    if (resolvedOnes.length > 0) {
      row.resolved += 1;
      if (resolvedOnes.some((m) => m.label === "sold")) row.won += 1;
      if (resolvedOnes.some((m) => m.label === "booked")) row.booked += 1;
    } else if (mine.some((m) => m.label === "ghosted")) {
      row.ghostsExcluded += 1;
    } else {
      row.untaggedUnresolved += 1;
    }
  }

  for (const v of verdicts) {
    const band = placed.get(v.contactId);
    if (band === undefined) continue;
    const row = rows.get(band)!;
    if (v.verdict === "real_buyer") row.ownerVerdicts.realBuyer += 1;
    else row.ownerVerdicts.timeWaster += 1;
  }

  return QUALIFY_BANDS.map((b) => rows.get(b)!);
}

/** The report lines: counts only, never a rate and never an id. */
export function formatQualifyCalibration(rows: readonly QualifyCalibrationRow[]): string[] {
  const lines = [
    "ASSOCIATION, NOT LIFT. Nothing chose who got which band and nothing routes on it. A count here says what happened to leads scored so, not what the band caused.",
    "A contact counts once, in its latest band before its first standing outcome. Untagged leads are unresolved. Ghosts are excluded.",
    "",
  ];
  for (const r of rows) {
    lines.push(
      `${r.band.padEnd(11)} scored ${r.scored}, resolved ${r.resolved}, won ${r.won}, booked ${r.booked}, ` +
        `untagged unresolved ${r.untaggedUnresolved}, ghosts excluded ${r.ghostsExcluded}, ` +
        `owner verdicts: real buyer ${r.ownerVerdicts.realBuyer}, time waster ${r.ownerVerdicts.timeWaster}`,
    );
  }
  lines.push("", "show rate: no source yet", "auto-demote: not wired (a documented hook, QUALIFY_DEMOTE_HOOK)");
  return lines;
}

/**
 * G75 (Master Spec B13 items 2 and 7: calibration audits and inference
 * accounting). The research card's inferences against what happened, RECORD
 * ONLY: nothing here moves a prior, writes a row or feeds a learner
 * (`INFERENCE_PRIORS` stays a code table; row 154). Same placement as
 * `calibrateQualify`: a contact counts once, on its latest card built at or
 * before its first standing resolved outcome, or its latest card when it has
 * none; a contact whose first outcome precedes every card is placed nowhere.
 *
 * A code is MEASURED only when an outcome can say whether it was right. The
 * one check the data supports is an interest code against the inquiry type the
 * owner recorded on the outcome, through `RESEARCH_INTEREST_SCENARIO`. Every
 * other code (tone, specificity, memory, an unmapped interest) reads
 * `not_measured`, never 0. While ACKRA_RESEARCH_MODEL is off there are no model
 * labels on any card, so the interest rows are empty, and the report says so.
 */
export interface ResearchCardFold {
  readonly contactId: string;
  readonly builtAt: Date;
  readonly inferences: readonly { readonly code: string; readonly method: string; readonly confidence: number }[];
}

/** An outcome fold with the inquiry type recorded on it, or null when none was. */
export interface ResearchOutcomeFold extends OutcomeFold {
  readonly inquiryType: string | null;
}

/**
 * G75 (TA-1291). The interest codes an outcome's inquiry type can confirm.
 * Only repair maps without a guess: a ring, a band or a watch can be custom or
 * ready made, so those codes are not_measured rather than scored against a
 * scenario they may not belong to (section 0).
 */
export const RESEARCH_INTEREST_SCENARIO: Readonly<Record<string, InquiryType>> = Object.freeze({
  "interest.repair_item": "repair",
});

export interface ResearchCalibrationRow {
  readonly code: string;
  readonly method: string;
  /** The confidence every card stored for this code, or null when they differ (never an average). */
  readonly prior: number | null;
  /** Placed contacts whose card carries the code. */
  readonly cards: number;
  readonly status: "measured" | "not_measured";
  /** Measured only: of those, contacts whose first standing resolved outcome records an inquiry type. */
  readonly resolved: number | null;
  /** Measured only: of the resolved, the inquiry type is the code's mapped scenario. */
  readonly agreed: number | null;
  /** Measured only: no standing resolved outcome, or one with no inquiry type. */
  readonly noScenario: number | null;
}

const RESEARCH_RESOLVED_LABELS: readonly OutcomeFoldLabel[] = ["sold", "booked", "deposit", "lost"];

export function calibrateResearchInferences(
  cards: readonly ResearchCardFold[],
  outcomes: readonly ResearchOutcomeFold[],
): readonly ResearchCalibrationRow[] {
  const withdrawn = new Set<string>();
  for (const o of outcomes) if (o.kind === "reversed" || o.kind === "ghost_reversed") withdrawn.add(o.outcomeId);
  const firstResolved = new Map<string, ResearchOutcomeFold>();
  for (const o of outcomes) {
    if (o.kind !== "recorded" || o.label === null || withdrawn.has(o.outcomeId) || !RESEARCH_RESOLVED_LABELS.includes(o.label)) continue;
    const prev = firstResolved.get(o.contactId);
    if (prev === undefined || o.occurredAt.getTime() < prev.occurredAt.getTime()) firstResolved.set(o.contactId, o);
  }

  const cardsByContact = new Map<string, ResearchCardFold[]>();
  for (const c of cards) cardsByContact.set(c.contactId, [...(cardsByContact.get(c.contactId) ?? []), c]);

  type Acc = { code: string; method: string; priors: Set<number>; cards: number; resolved: number; agreed: number; noScenario: number };
  const acc = new Map<string, Acc>();
  for (const [contactId, list] of cardsByContact) {
    const outcome = firstResolved.get(contactId) ?? null;
    let pick: ResearchCardFold | null = null;
    for (const c of list) {
      if (outcome !== null && c.builtAt.getTime() > outcome.occurredAt.getTime()) continue;
      if (pick === null || c.builtAt.getTime() >= pick.builtAt.getTime()) pick = c;
    }
    if (pick === null) continue; // the outcome precedes every card: a card after the fact predicts nothing
    const seen = new Set<string>();
    for (const inf of pick.inferences) {
      // A code is a dotted closed-catalogue word and a method a snake_case word, so "|" never occurs in either.
      const key = `${inf.code}|${inf.method}`;
      const a = acc.get(key) ?? { code: inf.code, method: inf.method, priors: new Set<number>(), cards: 0, resolved: 0, agreed: 0, noScenario: 0 };
      a.priors.add(inf.confidence);
      if (!seen.has(key)) {
        seen.add(key);
        a.cards += 1;
        const scenario = RESEARCH_INTEREST_SCENARIO[inf.code];
        if (outcome === null || outcome.inquiryType === null) a.noScenario += 1;
        else {
          a.resolved += 1;
          if (scenario !== undefined && outcome.inquiryType === scenario) a.agreed += 1;
        }
      }
      acc.set(key, a);
    }
  }

  return [...acc.values()]
    .sort((x, y) => (x.code < y.code ? -1 : x.code > y.code ? 1 : x.method < y.method ? -1 : x.method > y.method ? 1 : 0))
    .map((a): ResearchCalibrationRow => {
      const measured = Object.prototype.hasOwnProperty.call(RESEARCH_INTEREST_SCENARIO, a.code);
      return {
        code: a.code,
        method: a.method,
        prior: a.priors.size === 1 ? [...a.priors][0]! : null,
        cards: a.cards,
        status: measured ? "measured" : "not_measured",
        resolved: measured ? a.resolved : null,
        agreed: measured ? a.agreed : null,
        noScenario: measured ? a.noScenario : null,
      };
    });
}

/** The report lines: counts only, never a rate and never an id. */
export function formatResearchCalibration(rows: readonly ResearchCalibrationRow[]): string[] {
  const lines = [
    "RESEARCH CARD INFERENCES, RECORD ONLY. This moves no prior and writes nothing (row 154). ASSOCIATION, NOT LIFT.",
    "A contact counts once, on its latest card before its first standing outcome. Only an interest an inquiry type can confirm is measured.",
  ];
  if (rows.length === 0) {
    lines.push("not_measured: no research card was read (ACKRA_RESEARCH_MODEL off, or no card built yet)");
    return lines;
  }
  for (const r of rows) {
    const head = `${r.code} (${r.method}, prior ${r.prior === null ? "varies" : String(r.prior)}): cards ${r.cards}`;
    lines.push(
      r.status === "measured"
        ? `${head}, resolved ${r.resolved ?? 0}, agreed ${r.agreed ?? 0}, no scenario ${r.noScenario ?? 0}`
        : `${head}, not_measured (no outcome says whether it was right)`,
    );
  }
  return lines;
}

export class QualifyDemoteNotWired extends Error {
  constructor() {
    super("QUALIFY_DEMOTE_HOOK is not wired: show rate has no source yet and no ruling lets a score demote itself");
    this.name = "QualifyDemoteNotWired";
  }
}

/** Documented hook for the band auto-demote (Agent Brains 5.3 L4). Throws if called: nothing may rely on it. */
export const QUALIFY_DEMOTE_HOOK = (): never => {
  throw new QualifyDemoteNotWired();
};
